import React, {useContext} from 'react';
import ReactDOM from 'react-dom/client';
import {createBrowserRouter, RouterProvider} from 'react-router-dom';
import ErrorPage from './error-page';
import * as Components from './components';
import {ContextProvider} from './context';
import './index.css';
import LoadingSpinner from './components/UI/LoadingSpinner';
import {Context} from './context';
import SimpleSnackbar from './components/UI/SimpleSnackbar';
import {scope} from './settings';

function Layout({withSideBar, children, doesNotRequireLoggin}) {
  const {
    token,
    account,
    localeText,
    snackBarMessage,
    setSnackBarMessage,
    content
  } = useContext(Context);

  // Show loading spinner while content is being fetched
  if (!content) {
    return (
      <div className='h-full'>
        <div className='h-screen w-full flex justify-center items-center'>
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  let contentToRender;
  if (doesNotRequireLoggin || (token && account)) {
    contentToRender = (
      <React.Fragment>
        <div className={`h-full ${withSideBar ? 'w-full xl:w-1/2' : 'w-full'}`}>
          {children}
        </div>
        {withSideBar && (
          <Components.SideBar className='w-full xl:block xl:w-1/2' />
        )}
      </React.Fragment>
    );
  } else if (token && !account) {
    contentToRender = (
      <div className='h-full w-full flex justify-center items-center'>
        <LoadingSpinner />
      </div>
    );
  } else {
    // No token case
    contentToRender = localeText('rw_msg_not_logged_in');
  }

  return (
    <div className='h-full'>
      <Components.Header className='fixed w-full top-0 overflow-hidden h-24 lg:h-32 z-10 bg-gradient-to-b via-white from-white sm:via-inherit sm:bg-dark sm:text-white' />
      <div
        className={`mt-24 lg:mt-32 mb-20 p-8 xl:px-32 py-5 lg:py-20 flex flex-col-reverse xl:flex-row justify-end xl:justify-start items-center xl:items-start gap-12 2xl:gap-24 min-h-[calc(100vh-145px)] sm:min-h-[calc(100vh-152px)] md:min-h-[calc(100vh-176px)] lg:min-h-[calc(100vh-208px)] ${
          token && !account && 'h-0'
        }`} /*min-h combined w/ h-0 seems to allow the LoadingSpinner's parent div's h-full to work properly*/
      >
        {contentToRender}
      </div>
      <SimpleSnackbar {...{snackBarMessage, setSnackBarMessage}} />
      <Components.Footer className='fixed w-full bottom-0 overflow-hidden md:h-20 z-10' />
    </div>
  );
}

// This is to support a temporary link from the mobile apps for mobile-first
// https://tssherpa.atlassian.net/browse/SV-1149
const riderwebAccOrders = {
  path: '/rider-web/account/orders',
  element: (
    <Layout doesNotRequireLoggin>
      <Components.NoOrderHistory />
    </Layout>
  ),
  errorElement: <ErrorPage />
};

const router = createBrowserRouter([
  {
    path: '/',
    element: (
      <Layout withSideBar={true} doesNotRequireLoggin>
        <Components.Welcome />
      </Layout>
    ),
    errorElement: <ErrorPage />
  },
  {
    path: '/loadfunds',
    element: (
      <Layout>
        <Components.LoadFunds />
      </Layout>
    ),
    errorElement: <ErrorPage />
  },
  {
    path: '/buy',
    element: (
      <Layout withSideBar={true}>
        {scope.includes('sunrail') ? (
          <Components.ProductSelector2 />
        ) : (
          <Components.ProductSelector1 />
        )}
      </Layout>
    ),
    errorElement: <ErrorPage />
  },
  {
    path: '/managecards',
    element: (
      <Layout>
        <Components.ManageCards />
      </Layout>
    ),
    errorElement: <ErrorPage />
  },
  {
    path: '/myaccount',
    element: (
      <Layout withSideBar={true}>
        <Components.MyAccount />
      </Layout>
    ),
    errorElement: <ErrorPage />
  },
  {
    path: '/myaccount/account-report',
    element: (
      <Layout>
        <Components.AccountReportPdf />
      </Layout>
    ),
    errorElement: <ErrorPage />
  },
  {
    path: '/usage-history/:ticketUuid',
    element: (
      <Layout>
        <Components.UsageHistory />
      </Layout>
    ),
    errorElement: <ErrorPage />
  },
  {
    path: '/myaccount/single-order-history/:orderid',
    element: (
      <Layout>
        <Components.SingleOrderHistory />
      </Layout>
    ),
    errorElement: <ErrorPage />
  },
  {
    path: '/mobile-activity-log',
    element: (
      <Layout>
        <Components.MobileActivityLog />
      </Layout>
    ),
    errorElement: <ErrorPage />
  },
  {
    path: '/checkout',
    element: (
      <Layout withSideBar={true}>
        <Components.Checkout />
      </Layout>
    ),
    errorElement: <ErrorPage />
  },
  {
    path: '/register',
    element: (
      <Layout doesNotRequireLoggin>
        <Components.Register />
      </Layout>
    ),
    errorElement: <ErrorPage />
  },
  {
    path: '/forgot',
    element: (
      <Layout doesNotRequireLoggin>
        <Components.ForgotPassword />
      </Layout>
    ),
    errorElement: <ErrorPage />
  },
  {
    path: '/rider-web/reset/*',
    element: (
      <Layout doesNotRequireLoggin>
        <Components.ResetPassword />
      </Layout>
    ),
    errorElement: <ErrorPage />
  },
  {
    path: '/login',
    element: (
      <Layout doesNotRequireLoggin>
        <Components.SignIn />
      </Layout>
    ),
    errorElement: <ErrorPage />
  },
  {
    path: '/migration',
    element: (
      <Layout doesNotRequireLoggin>
        <Components.Migration />
      </Layout>
    ),
    errorElement: <ErrorPage />
  },
  {
    path: '*',
    element: <Components.PageNotFound />,
    errorElement: <ErrorPage />
  },
  riderwebAccOrders
]);

ReactDOM.createRoot(document.getElementById('root')).render(
  <ContextProvider>
    <RouterProvider router={router} />
  </ContextProvider>
);
